import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blogImages = [
  "https://images.unsplash.com/photo-1466637574441-749b8f19452f?w=1200&q=80",
  "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=1200&q=80",
  "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1200&q=80"
];

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error('Error fetching blog post:', error);
        navigate('/blog');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug, navigate]);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 md:pt-32 bg-[#FAFAFA]" data-testid="blog-post-page">
        <div className="container-elegant py-16">
          <div className="max-w-3xl mx-auto animate-pulse space-y-8">
            <div className="h-8 bg-gray-100 rounded w-3/4" />
            <div className="aspect-video bg-gray-100 rounded-xl" />
            <div className="space-y-4">
              <div className="h-4 bg-gray-100 rounded w-full" />
              <div className="h-4 bg-gray-100 rounded w-full" />
              <div className="h-4 bg-gray-100 rounded w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen pt-24 md:pt-32 bg-[#FAFAFA]" data-testid="blog-post-page">
      <article className="container-elegant py-8 md:py-16">
        <div className="max-w-3xl mx-auto">
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-malva-dark hover:text-malva-darker transition-colors duration-300 mb-8"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>

          <header className="mb-8">
            <h1 className="font-serif text-3xl md:text-5xl text-gray-800 mb-6 leading-tight">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-500 text-sm">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4 text-malva" />
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-malva" />
                {formatDate(post.created_at)}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-malva" />
                5 min read
              </span>
            </div>
          </header>

          <div className="aspect-video rounded-xl overflow-hidden mb-10 shadow-lg">
            <img
              src={blogImages[0]}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            {post.content.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-gray-600 mb-6 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>

          <footer className="mt-16 pt-8 border-t border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <p className="text-gray-400 text-sm mb-2">Written by</p>
                <p className="text-gray-800 font-serif text-lg">{post.author}</p>
              </div>
              <Link 
                to="/blog"
                className="btn-secondary"
                data-testid="explore-more-articles"
              >
                Explore More Articles
              </Link>
            </div>
          </footer>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
